import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { getGuestConversations, getMessages, saveMessage, resetUnreadCount, getGuestUnreadCount } from '../../utils/db';
import { useNotification } from '../../contexts/NotificationContext';
import useNotificationPermission from '../../hooks/useNotificationPermission';
import NotificationPromptModal from '../../components/NotificationPromptModal';
import api from '../../utils/api';
import { registerFcmToken } from '../../utils/fcm';
import { getDeviceId } from '../../utils/deviceId';
import './Dashboard.css';

function GuestDashboard() {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [activeChat, setActiveChat] = useState(null); // null if no chat is open
  const [totalUnread, setTotalUnread] = useState(0);

  // 👇 MUST be called before any hook that depends on it
  const { addNotification } = useNotification();

  // Create socket INSIDE the component so it's not shared across instances
  const socketRef = useRef(null);
  if (!socketRef.current) {
    const serverUrl = process.env.REACT_APP_SOCKET_URL || `https://${window.location.hostname || '192.168.0.102'}:8084`;
    socketRef.current = io(serverUrl, {
      autoConnect: false,
    });
  }
  const socket = socketRef.current;

  // When notification permission is granted (either immediately if already
  // granted, or after the user clicks "Enable Notifications" in the modal),
  // generate the FCM token and register it with the backend, mapped to the
  // device ID so the backend can send push notifications to this device even
  // when the user is not authenticated (guest mode).
  const handleNotificationGranted = useCallback(async () => {
    try {
      const deviceId = getDeviceId();
      await registerFcmToken(deviceId);
      console.log('📱 Guest FCM registered with device ID:', deviceId);
    } catch (err) {
      console.warn('📱 Guest FCM registration failed:', err.message);
      // Show a user-facing notification so the user knows push notifications
      // may not work. This can happen if the service worker fails to activate
      // or if the browser blocks push subscriptions.
      addNotification(
        '🔔 Push notifications could not be set up. You may miss messages when the app is in the background.',
        'warning',
        8000
      );
    }
  }, [addNotification]);

  const {
    showPrompt,
    isPrompting,
    dismissPrompt,
    requestPermission,
    phase,
    showModal,
  } = useNotificationPermission({
    autoRequest: true,
    onGranted: handleNotificationGranted,
  });
  // autoRequest: true — auto-shows the explanation modal on mount when
  // permission is not yet granted. The user still clicks "Enable Notifications"
  // to trigger the browser's native permission dialog.

  // Load guest conversations and unread counts
  const loadConversations = useCallback(async () => {
    try {
      const deviceId = getDeviceId();
      const chats = await getGuestConversations(deviceId);
      setConversations(chats);
      const unread = await getGuestUnreadCount(deviceId);
      setTotalUnread(unread);
    } catch (err) {
      console.error('Failed to load guest conversations:', err);
    }
  }, []);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Connect socket when a chat is active
  useEffect(() => {
    if (!activeChat) {
      socket.disconnect();
      return;
    }

    socket.connect();

    socket.onAny((event, ...args) => {
      console.log(`[Guest Socket Event] ${event}`, args.length > 0 ? args[0] : '');
    });

    socket.on('connect', () => {
      console.log('Guest socket connected');
      // Register as anonymous user with the participant ID from this conversation
      socket.emit('register_user', {
        user_id: activeChat.userId,
        kind: 'anonymous',
      });
      // Join the conversation room
      socket.emit('join_room', activeChat.id);
    });

    return () => {
      socket.off('connect');
      socket.offAny();
      socket.disconnect();
    };
  }, [activeChat, socket]);

  const handleSelectChat = async (chat) => {
    // Reset unread count when opening a chat
    try {
      await resetUnreadCount(chat.id);
    } catch (err) {
      console.error('Failed to reset unread count:', err);
    }
    setActiveChat(chat);
  };

  const handleBackToChats = () => {
    setActiveChat(null);
  };

  // Show popup notification and navigate to login page
  const handleCreateOwnLink = () => {
    addNotification(
      '🔐 You need to log in first to create your own invite link. Sign in with Google to get started!',
      'warning',
      5000
    );
    navigate('/');
  };

  // If a chat is active, show the conversation window
  if (activeChat) {
    return (
      <GuestConversationWindow
        chat={activeChat}
        onBack={handleBackToChats}
        socket={socket}
      />
    );
  }

  return (
    <div className="dashboard-container">
      {/* Notification permission modal overlay */}
      <NotificationPromptModal
        show={showPrompt}
        isPrompting={isPrompting}
        onEnable={requestPermission}
        onDismiss={dismissPrompt}
        isDenied={phase === 'denied'}
      />

      {/* Floating notification bell — shown when permission is not yet granted */}
      {phase !== 'granted' && phase !== 'unsupported' && (
        <button
          onClick={showModal}
          style={{
            position: 'fixed',
            bottom: 80,
            right: 16,
            zIndex: 9999,
            width: 48,
            height: 48,
            borderRadius: '50%',
            border: 'none',
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            color: '#fff',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 20px rgba(99, 102, 241, 0.4)',
            transition: 'transform 0.2s, box-shadow 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.1)';
            e.currentTarget.style.boxShadow = '0 6px 28px rgba(99, 102, 241, 0.6)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(99, 102, 241, 0.4)';
          }}
          aria-label="Enable notifications"
          title={phase === 'denied' ? 'Notifications are blocked — click to learn more' : 'Enable notifications'}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
        </button>
      )}

      <div className="dashboard-content">
        <div className="chat-history-container">
          <div className="chat-history-header">
            <h1>
              Your Chats
              {totalUnread > 0 && (
                <span style={{
                  marginLeft: 8,
                  background: '#ef4444',
                  color: '#fff',
                  borderRadius: 10,
                  padding: '2px 8px',
                  fontSize: 12,
                  fontWeight: 600,
                  verticalAlign: 'middle',
                }}>
                  {totalUnread > 99 ? '99+' : totalUnread} new
                </span>
              )}
            </h1>
            <p>
              {conversations.length > 0
                ? `You have ${conversations.length} active chat${conversations.length !== 1 ? 's' : ''}.`
                : 'No chats yet. Join an invite link to start chatting!'}
            </p>
          </div>

          <div className="chat-list">
            {conversations.map(chat => (
              <div key={chat.id} className="chat-item" onClick={() => handleSelectChat(chat)}>
                <img src={chat.avatar} alt={chat.name} className="chat-avatar" />
                <div className="chat-info">
                  <h3 className="chat-name">
                    {chat.name}
                    {chat.unreadCount > 0 && (
                      <span style={{
                        marginLeft: 8,
                        background: '#ef4444',
                        color: '#fff',
                        borderRadius: 10,
                        padding: '1px 7px',
                        fontSize: 11,
                        fontWeight: 600,
                        verticalAlign: 'middle',
                      }}>
                        {chat.unreadCount > 99 ? '99+' : chat.unreadCount}
                      </span>
                    )}
                  </h3>
                  <p className="chat-guest-name" style={{ fontSize: 12, color: '#8b5cf6', margin: '2px 0' }}>
                    You're chatting as: <strong>{chat.guestName || 'You'}</strong>
                  </p>
                  <p className="chat-last-message">
                    {chat.lastMessage || 'No messages yet'}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Create your own link section */}
          <div style={{
            padding: '20px',
            borderTop: '1px solid rgba(255,255,255,0.05)',
            marginTop: 'auto',
          }}>
            <button
              className="new-chat-btn"
              onClick={handleCreateOwnLink}
              style={{
                width: '100%',
                padding: '14px',
                borderRadius: 12,
                border: 'none',
                background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                color: '#fff',
                fontSize: 16,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
              </svg>
              Create Your Own Invite Link
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Guest Conversation Window - similar to ConversationWindow but for guest users.
 * Shows the chat messages and allows sending messages.
 */
function GuestConversationWindow({ chat, onBack, socket }) {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(true);
  const [online, setOnline] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    const messagesArea = document.querySelector('.messages-area');
    if (messagesArea) {
      messagesArea.scrollTop = messagesArea.scrollHeight;
    }
  };

  // Load messages from IndexedDB
  useEffect(() => {
    const loadMessages = async () => {
      setLoading(true);
      try {
        // Reset unread count when opening the conversation
        try {
          await resetUnreadCount(chat.id);
        } catch (err) {
          console.error('Failed to reset unread count:', err);
        }

        const storedMessages = await getMessages(chat.id);
        setMessages(storedMessages);
      } catch (err) {
        console.error('Failed to load messages:', err);
        setMessages([]);
      } finally {
        setLoading(false);
      }
    };
    loadMessages();
  }, [chat.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const timer = setTimeout(scrollToBottom, 100);
    return () => clearTimeout(timer);
  }, []);

  // Re-fetch messages from IndexedDB. Used after socket reconnect to pick up
  // any messages that were saved by background handlers.
  const refreshMessages = useCallback(async () => {
    try {
      const storedMessages = await getMessages(chat.id);
      setMessages(storedMessages);
    } catch (err) {
      console.error('Failed to refresh messages:', err);
    }
  }, [chat?.id, setMessages]);

  // Join the conversation room when the chat opens, leave when it closes.
  // Also re-join the room when the socket reconnects (e.g., after a disconnect),
  // since Socket.IO does NOT automatically restore room membership on reconnect.
  useEffect(() => {
    if (!socket || !chat?.id) return;

    const room = chat.id;

    const handleConnect = () => {
      console.log(`🏠 [Guest] Re-joining conversation room after reconnect: ${room}`);
      socket.emit('join_room', room);
      // Re-fetch messages from IndexedDB after socket reconnect to pick up
      // any pending messages saved while the socket was disconnected.
      refreshMessages();
    };

    // If already connected, join immediately
    if (socket.connected) {
      console.log(`🏠 [Guest] Joining conversation room: ${room}`);
      socket.emit('join_room', room);
    }

    // Also join when the socket (re)connects
    socket.on('connect', handleConnect);

    return () => {
      console.log(`🚪 [Guest] Leaving conversation room: ${room}`);
      socket.emit('leave_room', room);
      socket.off('connect', handleConnect);
    };
  }, [socket, chat?.id, refreshMessages]);

  // Handle incoming socket messages
  useEffect(() => {
    if (!socket) return;

    socket.onAny((event, ...args) => {
      console.log(`[Guest Conv Socket Event] ${event}`, args.length > 0 ? args[0] : '');
    });

    const handleNewMessage = async (data) => {
      const isFromOther = data.user_id !== chat.userId;

      if (isFromOther) {
        const savedMsg = await saveMessage({
          conversationId: chat.id,
          text: data.message,
          sender: 'them',
          timestamp: data.timestamp || Date.now(),
        });
        setMessages(prev => [...prev, savedMsg]);
      }
    };

    const handlePendingMessage = async (data) => {
      const msgText = data.text || '';
      const msgSender = data.sender || 'them';

      if (msgSender !== 'me') {
        const savedMsg = await saveMessage({
          conversationId: chat.id,
          text: msgText,
          sender: 'them',
          timestamp: data.created_at || Date.now(),
        });
        setMessages(prev => {
          if (prev.some(m => m.id === savedMsg.id)) return prev;
          return [...prev, savedMsg];
        });
      }
    };

    const handleRoomJoined = () => {
      setOnline(true);
    };

    const handleUserOnline = () => {
      setOnline(true);
    };

    socket.on('new_message', handleNewMessage);
    socket.on('pending_message', handlePendingMessage);
    socket.on('room_joined', handleRoomJoined);
    socket.on('user_online', handleUserOnline);

    return () => {
      socket.off('new_message', handleNewMessage);
      socket.off('pending_message', handlePendingMessage);
      socket.off('room_joined', handleRoomJoined);
      socket.off('user_online', handleUserOnline);
    };
  }, [socket, chat.userId, chat.id]);

  const handleSend = async () => {
    if (!inputText.trim()) return;

    const text = inputText;
    setInputText('');

    // Save message to IndexedDB
    const savedMsg = await saveMessage({
      conversationId: chat.id,
      text,
      sender: 'me',
    });
    setMessages(prev => [...prev, savedMsg]);

    // Emit via socket if connected — the server handles Firestore persistence
    // for offline delivery. We do NOT call api.storeMessage() here because that
    // would create a duplicate entry in Firestore (the socket handler also stores it).
    if (socket && socket.connected) {
      socket.emit('chat_message', {
        user_id: chat.userId,
        message: text,
        timestamp: Date.now(),
        conversation_id: chat.id,
      });
    } else {
      // Fallback: if socket is not connected, store via HTTP API instead
      try {
        await api.storeMessage({
          conversationId: chat.id,
          text,
          senderId: chat.userId,
          senderName: chat.guestName || 'Guest',
          sender: 'me',
          chatType: 'anonymous',
        });
      } catch (err) {
        console.error('Failed to store message on server:', err);
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSend();
    }
  };

  return (
    <div className="conversation-container">
      <div className="conversation-header">
        <button className="back-btn" onClick={onBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
        </button>
        <img src={chat.avatar} alt={chat.name} className="chat-avatar" style={{width: 36, height: 36, marginRight: 12}} />
        <div style={{ flex: 1 }}>
          <h3 className="chat-name" style={{ margin: 0 }}>{chat.name}</h3>
          <p style={{ margin: '2px 0 0 0', fontSize: 12, color: '#8b5cf6' }}>
            You: {chat.guestName || 'You'}
          </p>
        </div>
        <span style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: online ? '#4CAF50' : '#666',
          display: 'inline-block',
          marginLeft: 8,
        }} />
        <span style={{ fontSize: 12, color: '#a0aab2', marginLeft: 4 }}>
          {online ? 'Online' : 'Offline'}
        </span>
      </div>

      <div className="messages-area">
        {loading ? (
          <div style={{ textAlign: 'center', color: '#a0aab2', padding: 40 }}>
            Loading messages...
          </div>
        ) : messages.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#a0aab2', padding: 40 }}>
            No messages yet. Start the conversation!
          </div>
        ) : (
          messages.map(msg => (
            <div key={msg.id} className={`message-bubble ${msg.sender === 'me' ? 'sent' : 'received'}`}>
              {msg.text}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="message-input-area">
        <input
          type="text"
          className="message-input"
          placeholder="Type a message..."
          value={inputText}
          onChange={e => setInputText(e.target.value)}
          onKeyPress={handleKeyPress}
        />
        <button className="send-btn" onClick={handleSend} disabled={!inputText.trim()}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>
        </button>
      </div>
    </div>
  );
}

export default GuestDashboard;
